import { Component, EventEmitter, Input, Output } from '@angular/core';
import { JobFileLeg } from '@core/models/job-file/job-file-leg.model';

export interface BookingEngineCardData {
  from?: string;
  to?: string;
  jobId?: string;
  locationType?: string;
  activityType?: string;
  requestedArrival?: string;
  requestedDeparture?: string;
  estimatedDurationMin?: string;
}

@Component({
  selector: 'hmt-booking-engine-card',
  templateUrl: './booking-engine-card.component.html',
  standalone: false,
  styleUrls: ['./booking-engine-card.component.scss']
})
export class BookingEngineCardComponent {
  @Input() data?: BookingEngineCardData;
  @Input() legs?: JobFileLeg[];
  @Input() jobId?: string;
  @Output() legChanged = new EventEmitter<number>();

  private currentLegIndex = 0;

  get computedData(): BookingEngineCardData {
    if (this.legs && this.legs.length > 0) {
      const idx = Math.max(0, Math.min(this.currentLegIndex, this.legs.length - 1));
      const leg = this.legs[idx];
      const fmt = (d: Date | string | number | undefined) => {
        if (!d) { return undefined; }
        const dt = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
        const hours12 = (dt.getHours() % 12) || 12;
        const ampm = dt.getHours() >= 12 ? 'PM' : 'AM';
        const yyyy = dt.getFullYear();
        const mm = (dt.getMonth() + 1).toString().padStart(2, '0');
        const dd = dt.getDate().toString().padStart(2, '0');
        const hh = hours12.toString().padStart(2, '0');
        const mi = dt.getMinutes().toString().padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ampm}`;
      };
      const durationMin = (leg.loadingTime ?? 0) + (leg.travelingTime ?? 0);
      return {
        from: leg.startLocation?.name,
        to: leg.endLocation?.name,
        jobId: this.jobId ?? this.data?.jobId,
        locationType: undefined,
        activityType: (leg.activityType as unknown as string) ?? this.data?.activityType,
        requestedArrival: fmt(leg.startTimeEta),
        requestedDeparture: fmt(leg.endTimeEta),
        estimatedDurationMin: (() => {
          const start = new Date(leg.startTimeEta).getTime();
          const end = new Date(leg.endTimeEta).getTime();
          if (isNaN(start) || isNaN(end) || end < start) return this.data?.estimatedDurationMin;

          const diffMs = end - start;
          const totalSeconds = Math.floor(diffMs / 1000);
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;

          let parts = [];
          if (hours > 0) parts.push(`${hours}h`);
          if (minutes > 0) parts.push(`${minutes} min`);
          if (seconds > 0) parts.push(`${seconds} sec`);
          if (parts.length === 0) parts.push('0 sec');
          return parts.join(' ');
        })()
      };
    }
    return this.data ?? {};
  }

  onProceed(): void {
    if (!this.legs || this.legs.length === 0) {
      return;
    }
    this.currentLegIndex = (this.currentLegIndex + 1) % this.legs.length;
    this.legChanged.emit(this.currentLegIndex);
  }

  onPrevious(): void {
    if (!this.legs || this.legs.length === 0) {
      return;
    }
    this.currentLegIndex = (this.currentLegIndex - 1 + this.legs.length) % this.legs.length;
    this.legChanged.emit(this.currentLegIndex);
  }
}


